import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { debounce, isEqual, isNull, mapKeys, uniq } from 'lodash';
import moment from 'moment-timezone';
import { Waypoint } from 'react-waypoint';

import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import { PrimaryButton } from '@magoosh/lib/button';
import Dropdown from '@magoosh/lib/dropdown';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import AnswerFlag from '@magoosh/organisms/answer_flag';
import { paths } from 'config/path_helpers';
import { AnswerData } from 'types/answer';
import { secondsFormattedAsMinutes } from 'utilities/time';

import ContextualLink from '@test_prep/app/components/contextual_link';
import * as api from '@test_prep/app/api';

import AnswerDifficulty from '../answer_difficulty';
import AnswerIcon from '../answer_icon';
import NoValueDash from '../../../components/no_value_dash';

import style from './style.module.scss';

interface Props {
  answers: AnswerData[]
  filters: any;
  onChange: (answers: AnswerData[]) => void;
}

export default (props: Props) => {
  const [page, setPage] = useState<number>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const restoredFromHistory = useRef(false);

  const navigateToAnswer = (e: React.MouseEvent, answerId: number) => {
    const filtersForUrl = mapKeys(props.filters, (value, key) => `filters[${key}]`);
    const url = paths.answer(answerId, filtersForUrl);
    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  };

  const saveToHistoryState = (answers, answersPage) => {
    window.history.replaceState({ answers, answersPage, scrollY: window.scrollY }, '');
  };

  const loadAnswers = (pageToFetch, clearAnswers = false) => {
    setIsLoading(true);
    if (clearAnswers) props.onChange([]);

    api.fetchAnswers(props.filters, pageToFetch).then((loadedAnswers) => {
      const newPage = loadedAnswers.length === 25 ? pageToFetch : null;
      setPage(newPage);

      const updatedAnswers = clearAnswers
        ? loadedAnswers
        : [...props.answers, ...loadedAnswers];

      props.onChange(updatedAnswers);
      saveToHistoryState(updatedAnswers, newPage);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    const historyState = window.history.state as any;
    const navType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type;
    const isBackForwardNavigation = navType === 'back_forward' || window.history.state?.__reactRouterPop;

    if (!restoredFromHistory.current && isBackForwardNavigation && historyState?.answers) {
      restoredFromHistory.current = true;
      props.onChange(historyState.answers);
      setPage(historyState.answersPage);
      requestAnimationFrame(() => window.scrollTo(0, historyState.scrollY || 0));
    } else {
      restoredFromHistory.current = true;
      window.history.replaceState(null, '');
      loadAnswers(1, true);
    }
  }, [props.filters]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const handleScroll = debounce(() => {
      if (window.history.state?.answers) {
        window.history.replaceState({ ...window.history.state, scrollY: window.scrollY }, '');
      }
    }, 100);

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  if (isMobile) {
    return (
      <div className="d-flex flex-column gap-s" style={{ padding: '20px 15px' }}>
        {
          props.answers.length > 0 || isLoading ? (
            <>
              {
                props.answers.map((answer, idx) => (
                  <React.Fragment key={answer.id}>
                    {
                      idx === props.answers.length - 20 && !isLoading && page && (
                        <Waypoint onEnter={() => loadAnswers(page + 1)} />
                      )
                    }

                    <div
                      className="d-flex flex-column gap-s"
                      onClick={(e) => navigateToAnswer(e, answer.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="align-items-center d-flex flex-1 gap-xs">
                        <div className="d-flex justify-content-center" style={{ width: 24 }}>
                          <AnswerIcon answer={answer} />
                        </div>

                        <div className="flex-1" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <strong>{answer.prompt.question.title}</strong>
                        </div>

                        <div className="small u-margin-L-s">
                          {moment(answer.createdAt * 1000).tz(timeZone).format('MMM D')}
                        </div>

                        <span onClick={e => e.stopPropagation()}>
                          <Dropdown align="right">
                            <Dropdown.Trigger>
                              <FontAwesomeIcon
                                className="text-gray u-padding-A-xxs"
                                icon="ellipsis-h"
                              />
                            </Dropdown.Trigger>

                            <Dropdown.Content>
                              <ul className="list-unstyled u-margin-A-n">
                                <li className="u-padding-H-s u-padding-V-xxs">
                                  <AnswerFlag
                                    flagged={answer.isFlagged}
                                    promptId={answer.prompt.id}
                                    showText
                                  />
                                </li>

                                {
                                  answer.scoreReport && (
                                    <li className="u-padding-H-s u-padding-V-xxs">
                                      <ContextualLink
                                        className="text-gray text-hover-no-decoration text-no-decoration"
                                        href={
                                          answer.scoreReport.requestType === 'essay' ? (
                                            paths.scoreReport(answer.scoreReport.id)
                                          ) : (
                                            paths.scoreReportAnswer(answer.scoreReport.id, answer.id)
                                          )
                                        }
                                      >
                                        Score Report
                                      </ContextualLink>
                                    </li>
                                  )
                                }

                                {
                                  answer.hasNote && (
                                    <li className="u-padding-H-s u-padding-V-xxs">
                                      <ContextualLink
                                        className="text-gray text-hover-no-decoration text-no-decoration"
                                        href={`${paths.answer(answer.id)}#note`}
                                        style={{ display: 'block', width: '100%' }}
                                      >
                                        View Note
                                      </ContextualLink>
                                    </li>
                                  )
                                }
                              </ul>
                            </Dropdown.Content>
                          </Dropdown>
                        </span>
                      </div>

                      <table className={cx(style.mobileTable, 'small')}>
                        <tr>
                          <td style={{ width: 120 }}>Section/Subject</td>
                          <td>{uniq([answer.examSection.name, ...answer.prompt.subjectTags]).join(', ')}</td>
                        </tr>

                        {
                          answer.humanDifficulty && (
                            <tr>
                              <td style={{ width: 120 }}>Difficulty</td>
                              <td><AnswerDifficulty answer={answer} /></td>
                            </tr>
                          )
                        }

                        {
                          !isNull(answer.time) && (
                            <tr>
                              <td style={{ width: 120 }}>Pace</td>
                              <td>{secondsFormattedAsMinutes(answer.time, false)}</td>
                            </tr>
                          )
                        }
                      </table>
                    </div>

                    {
                      idx !== props.answers.length - 1 && (
                        <hr className="u-margin-V-xxs" />
                      )
                    }
                  </React.Fragment>
                ))
              }

              {
                isLoading && (
                  <div className="d-flex justify-content-center">
                    <LoadingIndicator />
                  </div>
                )
              }

              {
                !isLoading && page && (
                  <div className="d-flex justify-content-center">
                    <PrimaryButton onClick={() => loadAnswers(page + 1)}>
                      Show More Answers
                    </PrimaryButton>
                  </div>
                )
              }
            </>
          ) : (
            <div className="align-items-center d-flex flex-1 flex-column gap-s u-margin-V-m">
              No answers found.
            </div>
          )
        }
      </div>
    );
  } else {
    return (
      <table className={style.table}>
        <thead>
          <th>Question</th>
          <th className={style.colSection}>Section</th>
          <th className={style.colSubject}>Subject</th>
          <th className={cx(style.colDifficulty, style.noWrap)}>Difficulty</th>
          <th className={cx(style.colYourPace, style.noWrap, 'text-center')}>Your Pace</th>
          <th className={cx(style.colOthersPace, style.noWrap, 'text-center visible-lg')}>Others' Pace</th>
          <th className={cx(style.colDate, style.noWrap, 'text-center')}>Date</th>
          <th className={cx(style.colNote, style.noWrap, 'text-center visible-lg')}>Note</th>
          <th className={cx(style.colFlag, style.noWrap, 'text-center visible-lg')}>Flag</th>
        </thead>

        <tbody>
          {
            props.answers.length > 0 || isLoading ? (
              <>
                {
                  props.answers.map((answer, idx) => (
                    <React.Fragment key={answer.id}>
                      <tr
                        className={style.clickableRow}
                        onClick={(e) => navigateToAnswer(e, answer.id)}
                      >
                        <td>
                          {
                            idx === props.answers.length - 20 && !isLoading && page && (
                              <Waypoint onEnter={() => loadAnswers(page + 1)} />
                            )
                          }

                          <div className="d-flex gap-s">
                            <div className="d-flex justify-content-center" style={{ minWidth: 24 }}>
                              <AnswerIcon answer={answer} />
                            </div>

                            <div className="d-flex flex-column">
                              <div>
                                <strong>{answer.prompt.question.title}</strong>
                              </div>

                              <div className="text-gray-light">
                                {
                                  answer.scoreReport && (
                                    <ContextualLink
                                      className="text-gray-light"
                                      href={
                                        answer.scoreReport.requestType === 'essay' ? (
                                          paths.scoreReport(answer.scoreReport.id)
                                        ) : ( 
                                          paths.scoreReportAnswer(answer.scoreReport.id, answer.id)
                                        )
                                      }
                                      onClick={e => e.stopPropagation()}
                                      style={{ color: '#2A6EB6' }}
                                    >
                                      Score Report
                                    </ContextualLink>
                                  )
                                }
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          {answer.examSection.name || <NoValueDash />}
                        </td>

                        <td>
                          {
                            answer.prompt.subjectTags.length > 0 ? (
                              answer.prompt.subjectTags.join(', ')
                            ) : <NoValueDash />
                          }
                        </td>

                        <td className={style.noWrap}>
                          <AnswerDifficulty answer={answer} />
                        </td>

                        <td className={cx(style.noWrap, 'text-center')}>
                          {
                            !isNull(answer.time) ? (
                              secondsFormattedAsMinutes(answer.time, false)
                            ) : <NoValueDash />
                          }
                        </td>

                        <td className={cx(style.noWrap, 'text-center visible-lg')}>
                          {
                            answer.prompt.correctAnswersAvgTime ? (
                              secondsFormattedAsMinutes(answer.prompt.correctAnswersAvgTime, false)
                            ) : <NoValueDash />
                          }
                        </td>

                        <td className={cx(style.noWrap, 'text-center')}>
                          {moment(answer.createdAt * 1000).tz(timeZone).format('MMM D')}
                        </td>

                        <td className={cx(style.noWrap, 'text-center visible-lg')} onClick={e => e.stopPropagation()}>
                          {
                            answer.hasNote ? (
                              <ContextualLink
                                className="text-gray text-hover-no-decoration text-no-decoration"
                                href={`${paths.answer(answer.id)}#note`}
                              >
                                <FontAwesomeIcon icon="pencil" />
                              </ContextualLink>
                            ) : (
                              <NoValueDash />
                            )
                          }
                        </td>

                        <td className={cx(style.noWrap, 'text-center visible-lg')} onClick={e => e.stopPropagation()}>
                          <AnswerFlag
                            className="justify-content-center"
                            flagged={answer.isFlagged}
                            promptId={answer.prompt.id}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                }

                {
                  isLoading && (
                    <tr>
                      <td colSpan={9}>
                        <div className="d-flex justify-content-center">
                          <LoadingIndicator />
                        </div>
                      </td>
                    </tr>
                  )
                }

                {
                  !isLoading && page && (
                    <tr>
                      <td colSpan={9}>
                        <div className="d-flex justify-content-center">
                          <PrimaryButton onClick={() => loadAnswers(page + 1)}>
                            Show More Answers
                          </PrimaryButton>
                        </div>
                      </td>
                    </tr>
                  )
                }
              </>
            ) : (
              <tr >
                <td colSpan={9}>
                  <div className="align-items-center d-flex flex-1 flex-column gap-s u-margin-V-m">
                    No answers found.
                  </div>
                </td>
              </tr>
            )
          }
        </tbody>
      </table>
    );
  }
};
